
import { useEffect, useRef, useState } from "react";
import {
    QueryClient,
    QueryClientProvider,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { Loading } from "@/components/Loading";
import { PageHeader } from "@/components/PageHeader";
import { SavedViews } from "@/components/SavedViews";
import {
    listPreferencesSchema,
    type ListPreferences,
} from "@/components/listPreferences";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import {
    providerViewSchema,
    type ProviderViewState,
} from "@/pages/Providers/listState";
import { ProviderDetail } from "@/pages/Providers/ProviderDetail";
import { ProviderEditor } from "@/pages/Providers/ProviderEditor";
import { ProviderStatusDialog } from "@/pages/Providers/ProviderStatusDialog";
import { ProviderTable } from "@/pages/Providers/ProviderTable";
import { Tabs, type TabItem } from "@/components/Tabs";
import { useProviders } from "@/pages/Providers/useProviders";
import {
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import type { DetailTab, Provider } from "@/pages/Providers/types";

interface ProvidersProps {
    basePath: string;
}

const ProvidersPage = ({ basePath }: ProvidersProps) => {
    const { t, token, user, language, router } = useProviderContext();
    const { request } = useProviderApi();
    const data = useProviders();
    const queryClient = useQueryClient();
    const [editor, setEditor] = useState<Provider | null | undefined>(
        undefined
    );
    const [status, setStatus] = useState<Provider | null>(null);
    const [detailTab, setDetailTab] = useState<DetailTab>("details");
    const [preferences, setPreferences] = useStoredPreference<ListPreferences>(
        `vigepra_provider_table:${user.id}:${user.company}`,
        listPreferencesSchema,
        {
            density: "comfortable",
            hidden: ["phone", "payment_blocked", "conflict_count"],
        }
    );
    const detailId =
        Number(router.pathname.match(/\/(?:view|fullview)\/(\d+)\/?$/)?.[1]) ||
        null;
    const listScroll = useRef(0);
    const wasDetail = useRef(false);
    useEffect(() => {
        if (detailId && !wasDetail.current) {
            listScroll.current = window.scrollY;
            window.scrollTo(0, 0);
        }
        if (!detailId && wasDetail.current) {
            window.scrollTo(0, listScroll.current);
        }
        wasDetail.current = Boolean(detailId);
    }, [detailId]);
    const detail = useQuery({
        queryKey: ["provider", token, user.company, language, detailId],
        queryFn: ({ signal }) =>
            request<{ item: Provider }>(`/${detailId}`, undefined, signal),
        enabled: detailId !== null,
        retry: false,
    });
    const view = (id: number, tab: DetailTab = "details"): void => {
        setDetailTab(tab);
        router.navigate(`${basePath}/view/${id}`);
    };
    const edit = (provider: Provider): void => {
        setEditor(provider);
    };
    const saved = (provider: Provider): void => {
        queryClient.setQueryData(
            ["provider", token, user.company, language, provider.id],
            { item: provider }
        );
        void queryClient.invalidateQueries({ queryKey: ["provider-history"] });
        setStatus(null);
        data.reload();
    };
    const editorSaved = (provider: Provider): void => {
        setEditor(undefined);
        saved(provider);
    };
    const catalogs = data.result?.catalogs;
    const summary = data.result?.summary;
    const viewState: ProviderViewState = {
        search: data.search,
        filters: data.filters,
        columnFilters: data.columnFilters,
        sorting: data.sorting,
        preferences,
        pageSize: data.pagination.pageSize,
    };
    const applyView = (state: ProviderViewState): void => {
        setPreferences(state.preferences);
        data.applyView(state);
    };
    const showSummary = (key: string): void => {
        data.applyView({
            search: data.search,
            filters: {
                ...data.filters,
                state: key === "active" || key === "inactive" ? key : "all",
                conflicts: key === "conflicts" ? "pending" : "all",
            },
            columnFilters: data.columnFilters,
            sorting: data.sorting,
            pageSize: data.pagination.pageSize,
        });
    };
    const activeSummary =
        data.filters.state === "all" && data.filters.conflicts === "pending"
            ? "conflicts"
            : data.filters.state === "active" ||
                data.filters.state === "inactive"
              ? data.filters.state
              : "all";
    const summaryTabs: TabItem<"all" | "active" | "inactive" | "conflicts">[] =
        (["all", "active", "inactive", "conflicts"] as const).map((key) => ({
            id: key,
            label: t(`providers.summary.${key}`),
            badge: summary?.[key === "all" ? "total" : key] ?? 0,
        }));

    return (
        <div className="space-y-3">
            <div className={detailId ? "hidden" : "space-y-3"}>
                <PageHeader
                    compact
                    className="!mb-0"
                    title={t("providers.menu.title")}
                    action={
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="ghost"
                                icon="refresh"
                                onClick={data.reload}
                                disabled={data.loading}
                                title={t("providers.ui.refresh")}
                                aria-label={t("providers.ui.refresh")}
                                onlyicon
                            />
                            <Button
                                variant="primary"
                                size="sm"
                                icon="plus"
                                onClick={() => setEditor(null)}
                                disabled={!catalogs}
                            >
                                {t("providers.ui.new")}
                            </Button>
                        </div>
                    }
                />
                {summary && (
                    <Tabs
                        tabs={summaryTabs}
                        activeTab={activeSummary}
                        onChange={showSummary}
                        variant="underline"
                        full={false}
                        scroll
                        syncUrl={false}
                        className="mb-3"
                    />
                )}
                {summary && summary.conflicts > 0 && (
                    <Alert variant="warning">
                        <div className="space-y-2">
                            <p>
                                {t("providers.ui.conflicts_banner", {
                                    count: summary.conflicts,
                                })}
                            </p>
                            <p>
                                {t("providers.ui.conflict_breakdown", {
                                    blocking: summary.blocking,
                                    warning: summary.warning,
                                })}
                            </p>
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={data.reviewConflicts}
                            >
                                {t("providers.ui.review_conflicts")}
                            </Button>
                        </div>
                    </Alert>
                )}
                {data.error && (
                    <Alert variant="error">
                        <p>{t(data.error)}</p>
                        <Button variant="link" onClick={data.reload}>
                            {t("providers.ui.retry")}
                        </Button>
                    </Alert>
                )}
                {data.loading && !data.result && (
                    <div className="relative min-h-32">
                        <Loading text={t("providers.ui.loading")} />
                    </div>
                )}
                {data.result && (
                    <ProviderTable
                        data={data}
                        onView={view}
                        onEdit={edit}
                        onStatus={setStatus}
                        preferences={preferences}
                        onPreferences={setPreferences}
                        views={(configuration) => (
                            <SavedViews<ProviderViewState>
                                storageKey={`vigepra_provider_views:${user.id}:${user.company}`}
                                schema={providerViewSchema}
                                value={viewState}
                                onApply={applyView}
                                configuration={configuration}
                            />
                        )}
                    />
                )}
            </div>
            {editor !== undefined && catalogs && (
                <ProviderEditor
                    key={editor?.id ?? "new"}
                    provider={editor ?? undefined}
                    catalogs={catalogs}
                    onClose={() => setEditor(undefined)}
                    onSaved={editorSaved}
                    onView={view}
                />
            )}
            {detailId && detail.isPending && (
                <Alert variant="info">{t("providers.ui.loading")}</Alert>
            )}
            {detailId && detail.isError && (
                <Alert variant="error">
                    <p>{t("providers.errors.detail")}</p>
                    <Button onClick={() => router.navigate(basePath)}>
                        {t("providers.ui.close")}
                    </Button>
                </Alert>
            )}
            {detailId && detail.data && catalogs && (
                <ProviderDetail
                    provider={detail.data.item}
                    catalogs={catalogs}
                    tab={detailTab}
                    onTab={setDetailTab}
                    onClose={() => router.navigate(basePath)}
                    onEdit={edit}
                    onStatus={setStatus}
                    onSaved={saved}
                    onView={view}
                    allowActions={editor === undefined}
                />
            )}
            {status && (
                <ProviderStatusDialog
                    key={status.id}
                    provider={status}
                    onClose={() => setStatus(null)}
                    onSaved={saved}
                />
            )}
        </div>
    );
};

export const Providers = ({ basePath }: ProvidersProps) => {
    const { user, token } = useProviderContext();
    const [client] = useState(() => new QueryClient());
    return (
        <QueryClientProvider client={client}>
            <ProvidersPage
                key={`${user.company}:${token}`}
                basePath={basePath}
            />
        </QueryClientProvider>
    );
};
